import {
  FULL_MAP_CONFIG,
  type FullMapPlaceImportance,
} from "../config/map.ts";
import {
  MINIMAP_CONFIG,
  type MinimapPalette,
} from "../config/minimap.ts";
import type { LocalHorizontal } from "../world/coordinates.ts";
import type {
  HorizontalBounds,
  PolygonWithHoles,
} from "../world/geometry.ts";
import {
  minimapCardinalDirection,
  minimapHeadingVector,
  type MinimapAreaFeature,
  type MinimapLineFeature,
  type MinimapTileFeatures,
} from "../world/minimap.ts";
import type { MinimapTone } from "./minimap.ts";
import {
  fullMapOverviewDrawRects,
  type FullMapOverview,
  type FullMapNeighborhood,
} from "./fullMapOverview.ts";

export interface FullMapViewModel {
  readonly playerPosition: Readonly<LocalHorizontal>;
  readonly playerFacingYaw: number;
  readonly playerLocationLabel: string | null;
  readonly marker: Readonly<LocalHorizontal> | null;
  readonly tone: MinimapTone;
  readonly worldBounds: Readonly<HorizontalBounds>;
  readonly overview: FullMapOverview | null;
  readonly neighborhoods: readonly FullMapNeighborhood[];
  readonly places: readonly FullMapPlace[];
  readonly tileRevision: number;
  readonly tiles: readonly MinimapTileFeatures[];
  readonly loadingTileCount: number;
  readonly loadErrorCount: number;
}

export interface FullMapPlace {
  readonly id: string;
  readonly label: string;
  readonly position: Readonly<LocalHorizontal>;
  readonly importance: FullMapPlaceImportance;
}

export interface FullMapViewport {
  readonly center: Readonly<LocalHorizontal>;
  readonly radiusM: number;
  readonly bounds: Readonly<HorizontalBounds>;
}

export interface FullMapCallbacks {
  onOpenChange(open: boolean): void;
  onViewportChange(viewport: Readonly<FullMapViewport>): void;
  onMarkerChange(marker: Readonly<LocalHorizontal> | null): void;
  onTeleport(marker: Readonly<LocalHorizontal>): Promise<void>;
}

export interface FullMapDiagnostics {
  readonly open: boolean;
  readonly ready: boolean;
  readonly center: LocalHorizontal;
  readonly radiusM: number;
  readonly marker: LocalHorizontal | null;
  readonly tileCount: number;
  readonly loadingTileCount: number;
  readonly loadErrorCount: number;
  readonly overviewReady: boolean;
  readonly visibleNeighborhoodCount: number;
  readonly visiblePlaceCount: number;
  readonly drawCount: number;
  readonly lastDrawMs: number;
  readonly maxDrawMs: number;
}

export interface FullMap {
  update(viewModel: Readonly<FullMapViewModel>): void;
  toggle(): void;
  close(): void;
  isOpen(): boolean;
  getDiagnostics(): FullMapDiagnostics;
  dispose(): void;
}

type FullMapConfig = typeof FULL_MAP_CONFIG;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export function fullMapVisibleBounds(
  center: Readonly<LocalHorizontal>,
  radiusM: number,
  aspectRatio: number,
): HorizontalBounds {
  const halfWidthM = radiusM * Math.max(1, aspectRatio);
  return {
    minX: center.x - halfWidthM,
    minZ: center.z - radiusM,
    maxX: center.x + halfWidthM,
    maxZ: center.z + radiusM,
  };
}

export function constrainFullMapCenter(
  center: Readonly<LocalHorizontal>,
  radiusM: number,
  aspectRatio: number,
  worldBounds: Readonly<HorizontalBounds>,
): LocalHorizontal {
  const halfWidthM = radiusM * Math.max(1, aspectRatio);
  const midpointX = (worldBounds.minX + worldBounds.maxX) / 2;
  const midpointZ = (worldBounds.minZ + worldBounds.maxZ) / 2;
  const minimumX = worldBounds.minX + halfWidthM;
  const maximumX = worldBounds.maxX - halfWidthM;
  const minimumZ = worldBounds.minZ + radiusM;
  const maximumZ = worldBounds.maxZ - radiusM;
  return {
    x: minimumX > maximumX
      ? midpointX
      : clamp(center.x, minimumX, maximumX),
    z: minimumZ > maximumZ
      ? midpointZ
      : clamp(center.z, minimumZ, maximumZ),
  };
}

export function fullMapPointAtPixel(
  pixel: Readonly<{ x: number; y: number }>,
  canvasSize: Readonly<{ width: number; height: number }>,
  center: Readonly<LocalHorizontal>,
  radiusM: number,
): LocalHorizontal {
  const metresPerPixel = radiusM * 2 / Math.max(1, canvasSize.height);
  return {
    x: center.x + (pixel.x - canvasSize.width / 2) * metresPerPixel,
    z: center.z + (pixel.y - canvasSize.height / 2) * metresPerPixel,
  };
}

function createElement<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tagName);
  element.className = className;
  return element;
}

function boundsIntersect(
  left: Readonly<HorizontalBounds>,
  right: Readonly<HorizontalBounds>,
): boolean {
  return left.minX <= right.maxX && left.maxX >= right.minX &&
    left.minZ <= right.maxZ && left.maxZ >= right.minZ;
}

function projectedPoint(
  point: Readonly<LocalHorizontal>,
  center: Readonly<LocalHorizontal>,
  metresPerPixel: number,
  width: number,
  height: number,
): { x: number; y: number } {
  return {
    x: width / 2 + (point.x - center.x) / metresPerPixel,
    y: height / 2 + (point.z - center.z) / metresPerPixel,
  };
}

function appendRing(
  path: Path2D,
  ring: readonly Readonly<LocalHorizontal>[],
  center: Readonly<LocalHorizontal>,
  metresPerPixel: number,
  width: number,
  height: number,
): void {
  const first = ring[0];
  if (first === undefined) return;
  const projectedFirst = projectedPoint(
    first,
    center,
    metresPerPixel,
    width,
    height,
  );
  path.moveTo(projectedFirst.x, projectedFirst.y);
  for (let index = 1; index < ring.length; index += 1) {
    const point = ring[index];
    if (point === undefined) continue;
    const projected = projectedPoint(
      point,
      center,
      metresPerPixel,
      width,
      height,
    );
    path.lineTo(projected.x, projected.y);
  }
  path.closePath();
}

function polygonPath(
  polygon: Readonly<PolygonWithHoles>,
  center: Readonly<LocalHorizontal>,
  metresPerPixel: number,
  width: number,
  height: number,
): Path2D {
  const path = new Path2D();
  appendRing(path, polygon.outer, center, metresPerPixel, width, height);
  for (const hole of polygon.holes) {
    appendRing(path, hole, center, metresPerPixel, width, height);
  }
  return path;
}

function drawAreas(
  context: CanvasRenderingContext2D,
  features: readonly MinimapAreaFeature[],
  center: Readonly<LocalHorizontal>,
  metresPerPixel: number,
  width: number,
  height: number,
  fillStyle: string,
  strokeStyle: string | null,
  strokeWidthPx: number,
): void {
  context.fillStyle = fillStyle;
  context.strokeStyle = strokeStyle ?? fillStyle;
  context.lineWidth = strokeWidthPx;
  for (const feature of features) {
    for (const polygon of feature.polygons) {
      const path = polygonPath(
        polygon,
        center,
        metresPerPixel,
        width,
        height,
      );
      context.fill(path, "evenodd");
      if (strokeStyle !== null && strokeWidthPx > 0) context.stroke(path);
    }
  }
}

function drawLines(
  context: CanvasRenderingContext2D,
  features: readonly MinimapLineFeature[],
  center: Readonly<LocalHorizontal>,
  metresPerPixel: number,
  width: number,
  height: number,
  strokeStyle: string,
  lineWidth: (feature: Readonly<MinimapLineFeature>) => number,
  dash: readonly number[] = [],
): void {
  context.strokeStyle = strokeStyle;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.setLineDash([...dash]);
  for (const feature of features) {
    const first = feature.points[0];
    if (first === undefined) continue;
    const path = new Path2D();
    const projectedFirst = projectedPoint(
      first,
      center,
      metresPerPixel,
      width,
      height,
    );
    path.moveTo(projectedFirst.x, projectedFirst.y);
    for (let index = 1; index < feature.points.length; index += 1) {
      const point = feature.points[index];
      if (point === undefined) continue;
      const projected = projectedPoint(
        point,
        center,
        metresPerPixel,
        width,
        height,
      );
      path.lineTo(projected.x, projected.y);
    }
    context.lineWidth = lineWidth(feature);
    context.stroke(path);
  }
  context.setLineDash([]);
}

interface CanvasRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

function rectanglesOverlap(left: Readonly<CanvasRect>, right: Readonly<CanvasRect>): boolean {
  return left.x < right.x + right.width &&
    left.x + left.width > right.x &&
    left.y < right.y + right.height &&
    left.y + left.height > right.y;
}

function fullMapReservedLabelRects(
  playerPosition: Readonly<LocalHorizontal>,
  center: Readonly<LocalHorizontal>,
  metresPerPixel: number,
  width: number,
  height: number,
  pixelRatio: number,
): CanvasRect[] {
  const playerPoint = projectedPoint(
    playerPosition,
    center,
    metresPerPixel,
    width,
    height,
  );
  return [
    { x: 0, y: 0, width: 154 * pixelRatio, height: 112 * pixelRatio },
    {
      x: width - 118 * pixelRatio,
      y: 0,
      width: 118 * pixelRatio,
      height: 92 * pixelRatio,
    },
    {
      x: width - 178 * pixelRatio,
      y: height - 64 * pixelRatio,
      width: 178 * pixelRatio,
      height: 64 * pixelRatio,
    },
    {
      x: width / 2 - 18 * pixelRatio,
      y: height / 2 - 18 * pixelRatio,
      width: 36 * pixelRatio,
      height: 36 * pixelRatio,
    },
    {
      x: playerPoint.x - 18 * pixelRatio,
      y: playerPoint.y - 31 * pixelRatio,
      width: 132 * pixelRatio,
      height: 54 * pixelRatio,
    },
  ];
}

function drawFullMapNeighborhoods(
  context: CanvasRenderingContext2D,
  neighborhoods: readonly FullMapNeighborhood[],
  center: Readonly<LocalHorizontal>,
  radiusM: number,
  metresPerPixel: number,
  width: number,
  height: number,
  pixelRatio: number,
  palette: Readonly<MinimapPalette>,
  occupied: CanvasRect[],
): number {
  if (radiusM <= 600) return 0;
  const candidates = neighborhoods
    .map((neighborhood) => ({
      neighborhood,
      point: projectedPoint(
        neighborhood.position,
        center,
        metresPerPixel,
        width,
        height,
      ),
    }))
    .filter(({ point }) =>
      point.x >= 0 && point.y >= 0 && point.x <= width && point.y <= height
    )
    .sort((left, right) =>
      right.neighborhood.sampleCount - left.neighborhood.sampleCount ||
      left.neighborhood.label.localeCompare(right.neighborhood.label, "lt")
    );
  const fontSize = (radiusM > 7_000 ? 9.5 : radiusM > 2_000 ? 11 : 12) *
    pixelRatio;
  const labelHeight = (radiusM > 7_000 ? 14 : 17) * pixelRatio;
  context.save();
  context.font =
    `700 ${fontSize}px system-ui, -apple-system, sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.lineJoin = "round";
  let visibleCount = 0;
  for (const { neighborhood, point } of candidates) {
    const label = neighborhood.label.toLocaleUpperCase("lt-LT");
    const textWidth = context.measureText(label).width;
    const offsets = radiusM > 7_000
      ? [
          { x: 0, y: 0 },
          { x: 0, y: -20 },
          { x: 0, y: 20 },
          { x: -46, y: -17 },
          { x: 46, y: 17 },
          { x: -46, y: 17 },
          { x: 46, y: -17 },
          { x: 0, y: -38 },
          { x: 0, y: 38 },
        ]
      : [{ x: 0, y: 0 }];
    const placement = offsets
      .map((offset) => ({
        x: point.x + offset.x * pixelRatio,
        y: point.y + offset.y * pixelRatio,
      }))
      .find((candidate) => {
        const rect = {
          x: candidate.x - textWidth / 2 - 5 * pixelRatio,
          y: candidate.y - labelHeight / 2,
          width: textWidth + 10 * pixelRatio,
          height: labelHeight,
        };
        return rect.x >= 4 * pixelRatio && rect.y >= 4 * pixelRatio &&
          rect.x + rect.width <= width - 4 * pixelRatio &&
          rect.y + rect.height <= height - 4 * pixelRatio &&
          !occupied.some((other) => rectanglesOverlap(rect, other));
      });
    if (placement === undefined) continue;
    const rect = {
      x: placement.x - textWidth / 2 - 5 * pixelRatio,
      y: placement.y - labelHeight / 2,
      width: textWidth + 10 * pixelRatio,
      height: labelHeight,
    };
    occupied.push(rect);
    if (
      Math.abs(placement.x - point.x) > 8 * pixelRatio ||
      Math.abs(placement.y - point.y) > 8 * pixelRatio
    ) {
      context.beginPath();
      context.moveTo(point.x, point.y);
      context.lineTo(placement.x, placement.y);
      context.globalAlpha = 0.24;
      context.strokeStyle = palette.playerOutline;
      context.lineWidth = 0.7 * pixelRatio;
      context.stroke();
    }
    context.globalAlpha = radiusM > 7_000 ? 0.58 : 0.68;
    context.strokeStyle = palette.ground;
    context.lineWidth = 4 * pixelRatio;
    context.strokeText(label, placement.x, placement.y);
    context.fillStyle = palette.playerOutline;
    context.fillText(label, placement.x, placement.y);
    visibleCount += 1;
    if (radiusM > 7_000 && visibleCount >= 6) break;
  }
  context.restore();
  return visibleCount;
}

export function fullMapMaximumPlaceImportance(
  radiusM: number,
): FullMapPlaceImportance {
  return radiusM > 4_000 ? 1 : radiusM > 300 ? 2 : 3;
}

function drawFullMapPlaces(
  context: CanvasRenderingContext2D,
  places: readonly FullMapPlace[],
  playerPosition: Readonly<LocalHorizontal>,
  center: Readonly<LocalHorizontal>,
  radiusM: number,
  metresPerPixel: number,
  width: number,
  height: number,
  pixelRatio: number,
  palette: Readonly<MinimapPalette>,
  occupied: CanvasRect[],
): number {
  const maximumImportance = fullMapMaximumPlaceImportance(radiusM);
  const candidates = places
    .filter((place) => place.importance <= maximumImportance)
    .map((place) => ({
      place,
      point: projectedPoint(
        place.position,
        center,
        metresPerPixel,
        width,
        height,
      ),
      distanceFromPlayerM: Math.hypot(
        place.position.x - playerPosition.x,
        place.position.z - playerPosition.z,
      ),
    }))
    .filter(({ point }) =>
      point.x >= 0 && point.y >= 0 && point.x <= width && point.y <= height
    )
    .sort((left, right) =>
      left.place.importance - right.place.importance ||
      left.distanceFromPlayerM - right.distanceFromPlayerM ||
      left.place.label.localeCompare(right.place.label, "lt")
    );

  const fontSize = 10 * pixelRatio;
  const labelHeight = 15 * pixelRatio;
  const labelGap = 8 * pixelRatio;
  context.save();
  context.font = `750 ${fontSize}px "Iowan Old Style", "Palatino Linotype", serif`;
  context.textBaseline = "middle";
  context.lineJoin = "round";
  let visibleLabelCount = 0;
  for (const { place, point } of candidates) {
    const textWidth = context.measureText(place.label).width;
    const placements = [
      { x: point.x + labelGap, y: point.y - 9 * pixelRatio },
      { x: point.x + labelGap, y: point.y + 10 * pixelRatio },
      { x: point.x - labelGap - textWidth, y: point.y - 9 * pixelRatio },
      { x: point.x - labelGap - textWidth, y: point.y + 10 * pixelRatio },
    ];
    const placement = placements.find(({ x, y }) => {
      const rect = {
        x: x - 3 * pixelRatio,
        y: y - labelHeight / 2,
        width: textWidth + 6 * pixelRatio,
        height: labelHeight,
      };
      return rect.x >= 4 * pixelRatio && rect.y >= 4 * pixelRatio &&
        rect.x + rect.width <= width - 4 * pixelRatio &&
        rect.y + rect.height <= height - 4 * pixelRatio &&
        !occupied.some((other) => rectanglesOverlap(rect, other));
    });
    if (placement === undefined) continue;
    const rect = {
      x: placement.x - 3 * pixelRatio,
      y: placement.y - labelHeight / 2,
      width: textWidth + 6 * pixelRatio,
      height: labelHeight,
    };
    occupied.push(rect);
    context.beginPath();
    context.arc(point.x, point.y, 3.2 * pixelRatio, 0, Math.PI * 2);
    context.fillStyle = palette.ground;
    context.strokeStyle = palette.player;
    context.lineWidth = 1.6 * pixelRatio;
    context.fill();
    context.stroke();
    context.beginPath();
    context.moveTo(point.x, point.y);
    context.lineTo(
      placement.x < point.x ? placement.x + textWidth : placement.x,
      placement.y,
    );
    context.strokeStyle = palette.playerOutline;
    context.globalAlpha = 0.48;
    context.lineWidth = 0.75 * pixelRatio;
    context.stroke();
    context.globalAlpha = 1;
    context.strokeStyle = palette.ground;
    context.lineWidth = 4 * pixelRatio;
    context.strokeText(place.label, placement.x, placement.y);
    context.fillStyle = palette.playerOutline;
    context.fillText(place.label, placement.x, placement.y);
    visibleLabelCount += 1;
  }
  context.restore();
  return visibleLabelCount;
}

function formatDistance(distanceM: number): string {
  return distanceM < 1_000
    ? `${Math.round(distanceM)} m`
    : `${(distanceM / 1_000).toFixed(distanceM < 10_000 ? 1 : 0)} km`;
}

function niceScaleDistance(targetM: number): number {
  const exponent = Math.floor(Math.log10(Math.max(1, targetM)));
  const magnitude = 10 ** exponent;
  const normalized = targetM / magnitude;
  const step = normalized >= 5 ? 5 : normalized >= 2 ? 2 : 1;
  return step * magnitude;
}

function worldBoundsRadius(
  bounds: Readonly<HorizontalBounds>,
  aspectRatio: number,
): number {
  const widthM = bounds.maxX - bounds.minX;
  const heightM = bounds.maxZ - bounds.minZ;
  return Math.max(heightM / 2, widthM / (2 * Math.max(1, aspectRatio))) * 1.06;
}

function now(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

export function createFullMap(
  root: HTMLElement,
  callbacks: Readonly<FullMapCallbacks>,
  config: FullMapConfig = FULL_MAP_CONFIG,
): FullMap {
  const dialog = createElement("dialog", "full-map");
  dialog.setAttribute("aria-labelledby", "full-map-title");

  const header = createElement("header", "full-map-header");
  const headingGroup = createElement("div", "full-map-heading-group");
  const eyebrow = createElement("p", "full-map-eyebrow");
  eyebrow.textContent = "North-up city survey";
  const title = createElement("h2", "full-map-title");
  title.id = "full-map-title";
  title.textContent = "Vilnius field map";
  const instruction = createElement("p", "full-map-instruction");
  instruction.textContent =
    "Wheel to reveal more places · drag to explore · click to mark";
  headingGroup.append(eyebrow, title, instruction);
  const closeButton = createElement("button", "full-map-close");
  closeButton.type = "button";
  closeButton.setAttribute("aria-label", "Close map");
  closeButton.append("Close ");
  const closeKey = createElement("kbd", "full-map-close-key");
  closeKey.textContent = "M";
  closeButton.append(closeKey);
  header.append(headingGroup, closeButton);

  const viewport = createElement("div", "full-map-viewport");
  const canvas = createElement("canvas", "full-map-canvas");
  canvas.tabIndex = 0;
  canvas.setAttribute(
    "aria-label",
    "Interactive north-up Vilnius map. Landmark labels appear as you zoom. Drag or use arrow keys to pan, use the wheel or plus and minus keys to zoom, and press Enter to mark the map centre.",
  );
  const loading = createElement("p", "full-map-loading");
  loading.setAttribute("aria-live", "polite");
  const north = createElement("div", "full-map-north");
  north.setAttribute("aria-hidden", "true");
  north.textContent = "N";
  const mapCentre = createElement("span", "full-map-centre");
  mapCentre.setAttribute("aria-hidden", "true");

  const controls = createElement("div", "full-map-controls");
  const zoomInButton = createElement("button", "full-map-control");
  zoomInButton.type = "button";
  zoomInButton.textContent = "+";
  zoomInButton.setAttribute("aria-label", "Zoom in");
  const zoomOutButton = createElement("button", "full-map-control");
  zoomOutButton.type = "button";
  zoomOutButton.textContent = "−";
  zoomOutButton.setAttribute("aria-label", "Zoom out");
  const locateButton = createElement("button", "full-map-control full-map-locate");
  locateButton.type = "button";
  locateButton.textContent = "Me";
  locateButton.setAttribute("aria-label", "Show player at local scale");
  const fitButton = createElement("button", "full-map-control full-map-fit");
  fitButton.type = "button";
  fitButton.textContent = "All";
  fitButton.setAttribute("aria-label", "Show the entire map");
  controls.append(zoomInButton, zoomOutButton, locateButton, fitButton);

  const scale = createElement("div", "full-map-scale");
  const scaleRule = createElement("span", "full-map-scale-rule");
  const scaleLabel = createElement("span", "full-map-scale-label");
  scale.append(scaleRule, scaleLabel);
  viewport.append(canvas, loading, north, mapCentre, controls, scale);

  const footer = createElement("footer", "full-map-footer");
  const markerCopy = createElement("div", "full-map-marker-copy");
  const markerKicker = createElement("span", "full-map-marker-kicker");
  markerKicker.textContent = "Waypoint";
  const markerStatus = createElement("output", "full-map-marker-status");
  markerStatus.setAttribute("aria-live", "polite");
  markerCopy.append(markerKicker, markerStatus);
  const actions = createElement("div", "full-map-actions");
  const markCentreButton = createElement("button", "full-map-action");
  markCentreButton.type = "button";
  markCentreButton.textContent = "Mark centre";
  const clearButton = createElement("button", "full-map-action full-map-action--quiet");
  clearButton.type = "button";
  clearButton.textContent = "Clear";
  const teleportButton = createElement("button", "full-map-action full-map-teleport");
  teleportButton.type = "button";
  const lab = createElement("span", "full-map-lab");
  lab.textContent = "Lab";
  teleportButton.append(lab, " Teleport");
  actions.append(markCentreButton, clearButton, teleportButton);
  footer.append(markerCopy, actions);

  dialog.append(header, viewport, footer);
  root.append(dialog);

  let open = false;
  let disposed = false;
  let teleporting = false;
  let teleportError: string | null = null;
  let viewModel: FullMapViewModel | null = null;
  let center: LocalHorizontal = { x: 0, z: 0 };
  let radiusM: number = config.initialRadiusM;
  let renderFrame: number | null = null;
  let lastViewportKey = "";
  let dragState: {
    pointerId: number;
    startX: number;
    startY: number;
    previousX: number;
    previousY: number;
    moved: boolean;
  } | null = null;
  let diagnostics: FullMapDiagnostics = {
    open: false,
    ready: false,
    center: { ...center },
    radiusM,
    marker: null,
    tileCount: 0,
    loadingTileCount: 0,
    loadErrorCount: 0,
    overviewReady: false,
    visibleNeighborhoodCount: 0,
    visiblePlaceCount: 0,
    drawCount: 0,
    lastDrawMs: 0,
    maxDrawMs: 0,
  };

  function canvasAspectRatio(): number {
    return Math.max(1, canvas.clientWidth) / Math.max(1, canvas.clientHeight);
  }

  function constrainCenter(next: Readonly<LocalHorizontal>): LocalHorizontal {
    return viewModel === null
      ? { ...next }
      : constrainFullMapCenter(
          next,
          radiusM,
          canvasAspectRatio(),
          viewModel.worldBounds,
        );
  }

  function scheduleRender(): void {
    if (!open || disposed || renderFrame !== null) return;
    renderFrame = requestAnimationFrame(() => {
      renderFrame = null;
      render();
    });
  }

  function emitViewport(): void {
    if (viewModel === null || !open) return;
    const aspectRatio = canvasAspectRatio();
    const bounds = fullMapVisibleBounds(center, radiusM, aspectRatio);
    const key = [
      center.x.toFixed(2),
      center.z.toFixed(2),
      radiusM.toFixed(2),
      aspectRatio.toFixed(3),
    ].join(":");
    if (key === lastViewportKey) return;
    lastViewportKey = key;
    callbacks.onViewportChange({ center: { ...center }, radiusM, bounds });
  }

  function ensureCanvasSize(): {
    context: CanvasRenderingContext2D;
    width: number;
    height: number;
    pixelRatio: number;
  } | null {
    const cssWidth = Math.max(1, Math.round(canvas.clientWidth));
    const cssHeight = Math.max(1, Math.round(canvas.clientHeight));
    const pixelRatio = Math.min(
      config.maxDevicePixelRatio,
      Math.max(1, window.devicePixelRatio || 1),
    );
    const width = Math.max(1, Math.round(cssWidth * pixelRatio));
    const height = Math.max(1, Math.round(cssHeight * pixelRatio));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      lastViewportKey = "";
    }
    const context = canvas.getContext("2d");
    return context === null ? null : { context, width, height, pixelRatio };
  }

  function renderSurvey(
    context: CanvasRenderingContext2D,
    model: Readonly<FullMapViewModel>,
    visibleTiles: readonly MinimapTileFeatures[],
    width: number,
    height: number,
    pixelRatio: number,
    visibleBounds: Readonly<HorizontalBounds>,
  ): Readonly<{ visibleNeighborhoodCount: number; visiblePlaceCount: number }> {
    const palette: MinimapPalette = MINIMAP_CONFIG.palettes[model.tone];
    const metresPerPixel = radiusM * 2 / height;
    const lines = visibleTiles.flatMap((tile) => tile.lines);
    const areas = visibleTiles.flatMap((tile) => tile.areas);
    const roads = lines.filter((feature) => feature.kind === "road");
    const paths = lines.filter((feature) => feature.kind === "path");
    const railways = lines.filter((feature) => feature.kind === "railway");
    const waters = lines.filter((feature) => feature.kind === "water");
    const vegetation = areas.filter((feature) => feature.kind === "vegetation");
    const buildings = areas.filter((feature) => feature.kind === "building");
    const roadWidth = (feature: Readonly<MinimapLineFeature>): number =>
      clamp(
        (feature.widthM ?? 5) / metresPerPixel,
        1.3 * pixelRatio,
        7 * pixelRatio,
      );

    context.clearRect(0, 0, width, height);
    context.fillStyle = palette.ground;
    context.fillRect(0, 0, width, height);
    if (model.overview !== null) {
      const overviewImage = model.overview.images[model.tone];
      const rectangles = fullMapOverviewDrawRects(
        model.overview.bounds,
        visibleBounds,
        { width: overviewImage.width, height: overviewImage.height },
        { width, height },
      );
      if (rectangles !== null) {
        context.drawImage(
          overviewImage,
          rectangles.source.x,
          rectangles.source.y,
          rectangles.source.width,
          rectangles.source.height,
          rectangles.destination.x,
          rectangles.destination.y,
          rectangles.destination.width,
          rectangles.destination.height,
        );
      }
    }
    drawAreas(
      context,
      vegetation,
      center,
      metresPerPixel,
      width,
      height,
      palette.vegetation,
      null,
      0,
    );
    drawLines(
      context,
      waters,
      center,
      metresPerPixel,
      width,
      height,
      palette.water,
      (feature) => clamp(
        (feature.widthM ?? 8) / metresPerPixel,
        2 * pixelRatio,
        10 * pixelRatio,
      ),
    );
    context.save();
    context.globalAlpha = 0.34;
    drawLines(
      context,
      railways,
      center,
      metresPerPixel,
      width,
      height,
      palette.railwayCasing,
      () => 1.8 * pixelRatio,
    );
    drawLines(
      context,
      railways,
      center,
      metresPerPixel,
      width,
      height,
      palette.railway,
      () => 0.9 * pixelRatio,
      [3 * pixelRatio, 4 * pixelRatio],
    );
    context.restore();
    drawLines(
      context,
      roads,
      center,
      metresPerPixel,
      width,
      height,
      palette.roadCasing,
      (feature) => roadWidth(feature) + 1.5 * pixelRatio,
    );
    drawLines(
      context,
      roads,
      center,
      metresPerPixel,
      width,
      height,
      palette.road,
      roadWidth,
    );
    drawLines(
      context,
      paths,
      center,
      metresPerPixel,
      width,
      height,
      palette.path,
      () => 1.2 * pixelRatio,
      [3 * pixelRatio, 2.5 * pixelRatio],
    );
    drawAreas(
      context,
      buildings,
      center,
      metresPerPixel,
      width,
      height,
      palette.building,
      palette.buildingEdge,
      0.7 * pixelRatio,
    );

    const occupied = fullMapReservedLabelRects(
      model.playerPosition,
      center,
      metresPerPixel,
      width,
      height,
      pixelRatio,
    );
    const visibleNeighborhoodCount = drawFullMapNeighborhoods(
      context,
      model.neighborhoods,
      center,
      radiusM,
      metresPerPixel,
      width,
      height,
      pixelRatio,
      palette,
      occupied,
    );
    const visiblePlaceCount = drawFullMapPlaces(
      context,
      model.places,
      model.playerPosition,
      center,
      radiusM,
      metresPerPixel,
      width,
      height,
      pixelRatio,
      palette,
      occupied,
    );

    const playerPoint = projectedPoint(
      model.playerPosition,
      center,
      metresPerPixel,
      width,
      height,
    );
    const heading = minimapHeadingVector(model.playerFacingYaw);
    context.save();
    context.beginPath();
    context.arc(
      playerPoint.x,
      playerPoint.y,
      12 * pixelRatio,
      0,
      Math.PI * 2,
    );
    context.fillStyle = palette.ground;
    context.globalAlpha = 0.82;
    context.fill();
    context.globalAlpha = 1;
    context.strokeStyle = palette.player;
    context.lineWidth = 2 * pixelRatio;
    context.stroke();
    context.restore();
    context.save();
    context.translate(playerPoint.x, playerPoint.y);
    context.rotate(Math.atan2(heading.y, heading.x));
    context.beginPath();
    context.moveTo(8 * pixelRatio, 0);
    context.lineTo(-6 * pixelRatio, 5 * pixelRatio);
    context.lineTo(-3 * pixelRatio, 0);
    context.lineTo(-6 * pixelRatio, -5 * pixelRatio);
    context.closePath();
    context.fillStyle = palette.player;
    context.strokeStyle = palette.playerOutline;
    context.lineWidth = 1.2 * pixelRatio;
    context.fill();
    context.stroke();
    context.restore();

    const playerLabel = radiusM > 4_000 || model.playerLocationLabel === null
      ? "YOU ARE HERE"
      : `YOU · ${model.playerLocationLabel}`;
    const playerLabelFontSize = 10.5 * pixelRatio;
    context.save();
    context.font =
      `800 ${playerLabelFontSize}px "Iowan Old Style", "Palatino Linotype", serif`;
    context.textBaseline = "middle";
    context.lineJoin = "round";
    const playerLabelWidth = context.measureText(playerLabel).width;
    const playerLabelFitsRight =
      playerPoint.x + 18 * pixelRatio + playerLabelWidth < width - 8 * pixelRatio;
    const playerLabelX = playerLabelFitsRight
      ? playerPoint.x + 18 * pixelRatio
      : playerPoint.x - 18 * pixelRatio;
    const playerLabelY = playerPoint.y - 13 * pixelRatio;
    context.textAlign = playerLabelFitsRight ? "left" : "right";
    context.strokeStyle = palette.ground;
    context.lineWidth = 5 * pixelRatio;
    context.strokeText(playerLabel, playerLabelX, playerLabelY);
    context.fillStyle = palette.playerOutline;
    context.fillText(playerLabel, playerLabelX, playerLabelY);
    context.restore();

    if (model.marker !== null) {
      const markerPoint = projectedPoint(
        model.marker,
        center,
        metresPerPixel,
        width,
        height,
      );
      context.save();
      context.translate(markerPoint.x, markerPoint.y);
      context.beginPath();
      context.arc(0, -4 * pixelRatio, 6 * pixelRatio, 0, Math.PI * 2);
      context.moveTo(-4 * pixelRatio, 0);
      context.lineTo(0, 8 * pixelRatio);
      context.lineTo(4 * pixelRatio, 0);
      context.closePath();
      context.fillStyle = "oklch(65% 0.13 63)";
      context.strokeStyle = "oklch(20% 0.025 145)";
      context.lineWidth = 1.25 * pixelRatio;
      context.fill();
      context.stroke();
      context.restore();
    }
    return { visibleNeighborhoodCount, visiblePlaceCount };
  }

  function render(): void {
    if (!open || viewModel === null) return;
    const startedAt = now();
    center = constrainCenter(center);
    const canvasState = ensureCanvasSize();
    if (canvasState === null) return;
    const aspectRatio = canvasState.width / canvasState.height;
    const visibleBounds = fullMapVisibleBounds(center, radiusM, aspectRatio);
    const visibleTiles = radiusM <= config.detailedTileMaximumRadiusM
      ? viewModel.tiles.filter((tile) =>
        boundsIntersect(tile.bounds, visibleBounds)
      )
      : [];
    const { visibleNeighborhoodCount, visiblePlaceCount } = renderSurvey(
      canvasState.context,
      viewModel,
      visibleTiles,
      canvasState.width,
      canvasState.height,
      canvasState.pixelRatio,
      visibleBounds,
    );

    const metresPerCssPixel = radiusM * 2 / Math.max(1, canvas.clientHeight);
    const scaleM = niceScaleDistance(metresPerCssPixel * 100);
    scaleRule.style.width = `${scaleM / metresPerCssPixel}px`;
    scaleLabel.textContent = formatDistance(scaleM);
    const ready = viewModel.overview !== null || visibleTiles.length > 0;
    loading.hidden = viewModel.overview !== null || (
      ready && viewModel.loadingTileCount === 0 &&
      viewModel.loadErrorCount === 0
    );
    loading.textContent = viewModel.loadErrorCount > 0
      ? `${viewModel.loadErrorCount} survey tile${viewModel.loadErrorCount === 1 ? "" : "s"} could not be read.`
      : ready
        ? `Charting ${viewModel.loadingTileCount} nearby sheet${viewModel.loadingTileCount === 1 ? "" : "s"}…`
        : "Charting this part of the city…";

    if (teleportError !== null) {
      markerStatus.textContent = teleportError;
    } else if (viewModel.marker === null) {
      markerStatus.textContent = "Click the map to set a waypoint";
    } else {
      const deltaX = viewModel.marker.x - viewModel.playerPosition.x;
      const deltaZ = viewModel.marker.z - viewModel.playerPosition.z;
      const distanceM = Math.hypot(deltaX, deltaZ);
      const direction = minimapCardinalDirection(Math.atan2(deltaX, deltaZ));
      markerStatus.textContent = `${formatDistance(distanceM)} · ${direction}`;
    }
    clearButton.disabled = viewModel.marker === null || teleporting;
    teleportButton.disabled = viewModel.marker === null || teleporting;
    teleportButton.lastChild!.textContent = teleporting
      ? " Relocating…"
      : " Teleport";
    dialog.dataset.tone = viewModel.tone;
    dialog.dataset.ready = String(ready);
    dialog.dataset.marker = String(viewModel.marker !== null);
    dialog.dataset.visiblePlaces = String(visiblePlaceCount);
    dialog.dataset.visibleNeighborhoods = String(visibleNeighborhoodCount);
    dialog.dataset.radiusM = radiusM.toFixed(2);
    const drawMs = now() - startedAt;
    diagnostics = {
      open,
      ready,
      center: { ...center },
      radiusM,
      marker: viewModel.marker === null ? null : { ...viewModel.marker },
      tileCount: visibleTiles.length,
      loadingTileCount: viewModel.loadingTileCount,
      loadErrorCount: viewModel.loadErrorCount,
      overviewReady: viewModel.overview !== null,
      visibleNeighborhoodCount,
      visiblePlaceCount,
      drawCount: diagnostics.drawCount + 1,
      lastDrawMs: drawMs,
      maxDrawMs: Math.max(diagnostics.maxDrawMs, drawMs),
    };
    emitViewport();
  }

  function setCenter(next: Readonly<LocalHorizontal>): void {
    center = constrainCenter(next);
    scheduleRender();
  }

  function setRadius(nextRadiusM: number, anchor?: Readonly<{
    x: number;
    y: number;
  }>): void {
    if (viewModel === null) return;
    const next = clamp(
      nextRadiusM,
      config.minimumRadiusM,
      config.maximumRadiusM,
    );
    if (Math.abs(next - radiusM) < 0.001) return;
    if (anchor !== undefined) {
      const size = { width: canvas.clientWidth, height: canvas.clientHeight };
      const before = fullMapPointAtPixel(anchor, size, center, radiusM);
      const after = fullMapPointAtPixel(anchor, size, center, next);
      center = {
        x: center.x + before.x - after.x,
        z: center.z + before.z - after.z,
      };
    }
    radiusM = next;
    center = constrainCenter(center);
    scheduleRender();
  }

  function markerAtCanvasPixel(clientX: number, clientY: number): LocalHorizontal | null {
    if (viewModel === null) return null;
    const rect = canvas.getBoundingClientRect();
    const point = fullMapPointAtPixel(
      { x: clientX - rect.left, y: clientY - rect.top },
      { width: rect.width, height: rect.height },
      center,
      radiusM,
    );
    if (
      point.x < viewModel.worldBounds.minX ||
      point.x > viewModel.worldBounds.maxX ||
      point.z < viewModel.worldBounds.minZ ||
      point.z > viewModel.worldBounds.maxZ
    ) {
      return null;
    }
    return point;
  }

  function setOpen(nextOpen: boolean): void {
    if (disposed || open === nextOpen || (nextOpen && viewModel === null)) return;
    open = nextOpen;
    diagnostics = { ...diagnostics, open };
    if (open) {
      dialog.showModal();
      radiusM = clamp(
        worldBoundsRadius(viewModel!.worldBounds, canvasAspectRatio()),
        config.minimumRadiusM,
        config.maximumRadiusM,
      );
      center = constrainCenter({
        x: (viewModel!.worldBounds.minX + viewModel!.worldBounds.maxX) / 2,
        z: (viewModel!.worldBounds.minZ + viewModel!.worldBounds.maxZ) / 2,
      });
      diagnostics = {
        ...diagnostics,
        open,
        center: { ...center },
        radiusM,
      };
      callbacks.onOpenChange(true);
      scheduleRender();
      requestAnimationFrame(() => canvas.focus({ preventScroll: true }));
    } else {
      if (dialog.open) dialog.close();
      callbacks.onOpenChange(false);
    }
  }

  function onGlobalKeyDown(event: KeyboardEvent): void {
    if (event.code !== "KeyM" || event.repeat) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    setOpen(!open);
  }

  function onCancel(event: Event): void {
    event.preventDefault();
    setOpen(false);
  }

  function onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || dragState !== null) return;
    canvas.setPointerCapture(event.pointerId);
    dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      previousX: event.clientX,
      previousY: event.clientY,
      moved: false,
    };
    canvas.dataset.dragging = "true";
  }

  function onPointerMove(event: PointerEvent): void {
    if (dragState?.pointerId !== event.pointerId) return;
    const totalDistance = Math.hypot(
      event.clientX - dragState.startX,
      event.clientY - dragState.startY,
    );
    if (totalDistance >= config.dragThresholdCssPx) dragState.moved = true;
    if (dragState.moved) {
      const metresPerPixel = radiusM * 2 / Math.max(1, canvas.clientHeight);
      setCenter({
        x: center.x - (event.clientX - dragState.previousX) * metresPerPixel,
        z: center.z - (event.clientY - dragState.previousY) * metresPerPixel,
      });
    }
    dragState.previousX = event.clientX;
    dragState.previousY = event.clientY;
  }

  function finishPointer(event: PointerEvent, cancelled: boolean): void {
    if (dragState?.pointerId !== event.pointerId) return;
    const moved = dragState.moved;
    dragState = null;
    canvas.dataset.dragging = "false";
    if (canvas.hasPointerCapture(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId);
    }
    if (!cancelled && !moved) {
      const marker = markerAtCanvasPixel(event.clientX, event.clientY);
      if (marker !== null) {
        teleportError = null;
        callbacks.onMarkerChange(marker);
      }
    }
  }

  function onWheel(event: WheelEvent): void {
    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    setRadius(
      radiusM * Math.exp(event.deltaY * config.wheelZoomSensitivity),
      { x: event.clientX - rect.left, y: event.clientY - rect.top },
    );
  }

  function onCanvasKeyDown(event: KeyboardEvent): void {
    if (viewModel === null) return;
    const panM = radiusM * config.keyboardPanFraction;
    if (event.key === "ArrowLeft") setCenter({ x: center.x - panM, z: center.z });
    else if (event.key === "ArrowRight") setCenter({ x: center.x + panM, z: center.z });
    else if (event.key === "ArrowUp") setCenter({ x: center.x, z: center.z - panM });
    else if (event.key === "ArrowDown") setCenter({ x: center.x, z: center.z + panM });
    else if (event.key === "+" || event.key === "=") setRadius(radiusM / config.zoomStep);
    else if (event.key === "-" || event.key === "_") setRadius(radiusM * config.zoomStep);
    else if (event.key === "0") {
      radiusM = config.initialRadiusM;
      setCenter(viewModel.playerPosition);
    }
    else if (event.key === "Enter") {
      teleportError = null;
      callbacks.onMarkerChange({ ...center });
    } else if (event.key === "Delete" || event.key === "Backspace") {
      teleportError = null;
      callbacks.onMarkerChange(null);
    }
    else return;
    event.preventDefault();
  }

  async function onTeleportClick(): Promise<void> {
    if (teleporting || viewModel?.marker === null || viewModel === null) return;
    teleporting = true;
    teleportError = null;
    scheduleRender();
    try {
      await callbacks.onTeleport(viewModel.marker);
      setOpen(false);
    } catch (error) {
      teleportError = error instanceof Error
        ? error.message
        : "That location could not be reached.";
    } finally {
      teleporting = false;
      scheduleRender();
    }
  }

  closeButton.addEventListener("click", () => setOpen(false));
  dialog.addEventListener("cancel", onCancel);
  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", (event) => finishPointer(event, false));
  canvas.addEventListener("pointercancel", (event) => finishPointer(event, true));
  canvas.addEventListener("wheel", onWheel, { passive: false });
  canvas.addEventListener("keydown", onCanvasKeyDown);
  zoomInButton.addEventListener("click", () => setRadius(radiusM / config.zoomStep));
  zoomOutButton.addEventListener("click", () => setRadius(radiusM * config.zoomStep));
  locateButton.addEventListener("click", () => {
    if (viewModel === null) return;
    radiusM = config.initialRadiusM;
    setCenter(viewModel.playerPosition);
  });
  fitButton.addEventListener("click", () => {
    if (viewModel === null) return;
    radiusM = clamp(
      worldBoundsRadius(viewModel.worldBounds, canvasAspectRatio()),
      config.minimumRadiusM,
      config.maximumRadiusM,
    );
    center = {
      x: (viewModel.worldBounds.minX + viewModel.worldBounds.maxX) / 2,
      z: (viewModel.worldBounds.minZ + viewModel.worldBounds.maxZ) / 2,
    };
    scheduleRender();
  });
  markCentreButton.addEventListener("click", () => {
    teleportError = null;
    callbacks.onMarkerChange({ ...center });
  });
  clearButton.addEventListener("click", () => {
    teleportError = null;
    callbacks.onMarkerChange(null);
  });
  teleportButton.addEventListener("click", () => void onTeleportClick());
  window.addEventListener("keydown", onGlobalKeyDown, { capture: true });
  window.addEventListener("resize", scheduleRender);

  return {
    update(next): void {
      const previous = viewModel;
      const shouldRender = previous === null ||
        previous.tileRevision !== next.tileRevision ||
        previous.overview !== next.overview ||
        previous.tone !== next.tone ||
        previous.loadingTileCount !== next.loadingTileCount ||
        previous.loadErrorCount !== next.loadErrorCount ||
        previous.places !== next.places ||
        previous.playerLocationLabel !== next.playerLocationLabel ||
        previous.playerFacingYaw !== next.playerFacingYaw ||
        previous.playerPosition.x !== next.playerPosition.x ||
        previous.playerPosition.z !== next.playerPosition.z ||
        previous.marker?.x !== next.marker?.x ||
        previous.marker?.z !== next.marker?.z;
      viewModel = {
        ...next,
        playerPosition: { ...next.playerPosition },
        marker: next.marker === null ? null : { ...next.marker },
        worldBounds: { ...next.worldBounds },
        overview: next.overview,
        places: next.places,
        tiles: [...next.tiles],
      };
      diagnostics = {
        ...diagnostics,
        marker: next.marker === null ? null : { ...next.marker },
        loadingTileCount: next.loadingTileCount,
        loadErrorCount: next.loadErrorCount,
        overviewReady: next.overview !== null,
      };
      if (shouldRender) scheduleRender();
    },
    toggle(): void {
      setOpen(!open);
    },
    close(): void {
      setOpen(false);
    },
    isOpen(): boolean {
      return open;
    },
    getDiagnostics(): FullMapDiagnostics {
      return {
        ...diagnostics,
        center: { ...diagnostics.center },
        marker: diagnostics.marker === null ? null : { ...diagnostics.marker },
      };
    },
    dispose(): void {
      if (disposed) return;
      if (open) callbacks.onOpenChange(false);
      disposed = true;
      open = false;
      if (renderFrame !== null) cancelAnimationFrame(renderFrame);
      window.removeEventListener("keydown", onGlobalKeyDown, { capture: true });
      window.removeEventListener("resize", scheduleRender);
      dialog.remove();
    },
  };
}
